import { bootstrap } from './app/bootstrap.js';
import {
	destroyConfiguratorApp,
	initConfiguratorApp,
} from './app/register-events.js';

const CONFIG_RUNTIME_SELECTOR = 'script.sd-config-runtime';
const SWUP_PAGE_VIEW_EVENT = 'swup:page:view';
const SWUP_VISIT_START_EVENT = 'swup:visit:start';

/**
* Responsibility:
* - Resolve the rendered configurator root for the current page lifecycle entry.
*
* Touches:
 * - Uses the provided root when valid, otherwise resolves the nearest native Gravity Forms form from the runtime script node.
*
* Guarantees:
* - Returns one matching configurator root element or `null` when the configurator is not rendered on the page.
*/
function resolveConfiguratorRoot(configRoot) {
	if (configRoot instanceof HTMLFormElement) {
		return configRoot;
	}

	const runtimeScript = document.querySelector(CONFIG_RUNTIME_SELECTOR);
	if (!(runtimeScript instanceof HTMLScriptElement)) {
		return null;
	}

	const fallbackRoot = runtimeScript.closest('form[id^="gform_"]');
	return fallbackRoot instanceof HTMLFormElement ? fallbackRoot : null;
}

/**
* Responsibility:
* - Initialize the single-form configurator runtime for the current page markup.
*
* Touches:
* - Reads DOM runtime config from the resolved configurator root.
* - Hands the live app state to the shared configurator binder lifecycle.
*
* Guarantees:
* - Safely no-ops on pages where the configurator root is not rendered.
*/
export function initSdWitechsConfigurator(configRoot = null) {
	const resolvedConfigRoot = resolveConfiguratorRoot(configRoot);
	if (!(resolvedConfigRoot instanceof HTMLElement)) {
		return;
	}

	const app = bootstrap(resolvedConfigRoot);
	if (!app) {
		return;
	}

	initConfiguratorApp(app);
}

/**
* Responsibility:
* - Tear down the active single-form configurator runtime before a page swap.
*
* Touches:
* - Clears the shared active app reference used by configurator lifecycle listeners.
*
* Guarantees:
* - Leaves later configurator page views eligible for a clean re-initialization.
*/
export function destroySdWitechsConfigurator() {
	destroyConfiguratorApp();
}

/**
* Responsibility:
 * - Bootstrap the configurator runtime from the current document when the native Gravity Forms form root is rendered.
*
* Touches:
* - Delegates to `initSdWitechsConfigurator()` without requiring a theme-owned import path.
*
* Guarantees:
* - Safely no-ops on documents that do not render the configurator root.
*/
function bootConfiguratorRuntimeFromDocument() {
	initSdWitechsConfigurator();
}

/**
* Responsibility:
* - Clear the active configurator runtime before swup starts navigating away.
*
* Touches:
* - Delegates to `destroySdWitechsConfigurator()` so shared configurator listeners become inert.
*
* Guarantees:
* - Leaves later `swup:page:view` visits eligible for a fresh bootstrap.
*/
function teardownConfiguratorRuntimeForVisit() {
	destroySdWitechsConfigurator();
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', bootConfiguratorRuntimeFromDocument, {
		once: true,
	});
} else {
	bootConfiguratorRuntimeFromDocument();
}

document.addEventListener(SWUP_PAGE_VIEW_EVENT, bootConfiguratorRuntimeFromDocument);
document.addEventListener(SWUP_VISIT_START_EVENT, teardownConfiguratorRuntimeForVisit);
